import { useCallback } from 'react';
import { Tabs } from '@ui/components/Tabs';
import { Leaders } from '@features/landing/grandprix/ui/components/Leaders/Leaders';
import { LeadersListWidget } from '@features/landing/grandprix/ui/components/LeadersList';
import { LeadersModalWidget } from '@features/landing/grandprix/ui/components/LeadersModal';
import { LeaderboardParams } from '@features/landing/grandprix/utils/leaderboard';
import { useLeaderboard } from '@features/landing/grandprix/providers/LeaderboardProvider';

type Props = {
  preset?: 'landing' | 'club';
  size?: 'small' | 'medium' | 'large';
  onActionClick?: (params: LeaderboardParams) => void;
};

export function LeadersWidget({
  preset = 'landing',
  size = 'large',
  onActionClick,
}: Props) {
  const leaderboard = useLeaderboard();

  const limit = preset === 'landing' ? 10 : 3;
  const mobileLimit = preset === 'landing' ? 5 : 3;

  const onActionClickHandler = useCallback(
    ({ tillAge, clock, leaderboardType }: LeaderboardParams) => {
      if (onActionClick) {
        onActionClick({
          tillAge,
          clock,
          leaderboardType,
        });
        return;
      }
      leaderboard.openModal();
    },
    [onActionClick, leaderboard],
  );

  return (
    <>
      <Leaders
        preset={preset}
        size={size}
        clock={leaderboard.clock}
        tabs={
          <>
            <Tabs
              preset="landing"
              noTabMinWidth
              tabs={leaderboard.tillAgeTabs}
              value={leaderboard.tillAge}
              onChange={leaderboard.setTillAge}
            />
            {(size === 'medium' || size === 'small') && (
              <Tabs
                preset="landing"
                noTabMinWidth
                tabs={leaderboard.clockTabs}
                value={leaderboard.clock}
                onChange={leaderboard.setClock}
              />
            )}
            {size === 'small' && (
              <Tabs
                preset="landing"
                noTabMinWidth
                tabs={leaderboard.leaderboardTypeTabs}
                value={leaderboard.leaderboardType}
                onChange={leaderboard.setLeaderboardType}
              />
            )}
          </>
        }
        largeBlitzLists={
          <>
            <LeadersListWidget
              tillAge={leaderboard.tillAge}
              clock="blitz"
              leaderboardType="open"
              limit={limit}
              showTypeTitle
              onActionClick={onActionClickHandler}
              preset={preset}
            />
            <LeadersListWidget
              tillAge={leaderboard.tillAge}
              clock="blitz"
              leaderboardType="girls"
              limit={limit}
              showTypeTitle
              onActionClick={onActionClickHandler}
              preset={preset}
            />
          </>
        }
        largeRapidLists={
          <>
            <LeadersListWidget
              tillAge={leaderboard.tillAge}
              clock="rapid"
              leaderboardType="open"
              limit={limit}
              showTypeTitle
              onActionClick={onActionClickHandler}
              preset={preset}
            />
            <LeadersListWidget
              tillAge={leaderboard.tillAge}
              clock="rapid"
              leaderboardType="girls"
              limit={limit}
              showTypeTitle
              onActionClick={onActionClickHandler}
              preset={preset}
            />
          </>
        }
        mediumLists={
          <>
            <LeadersListWidget
              tillAge={leaderboard.tillAge}
              clock={leaderboard.clock}
              leaderboardType="open"
              limit={limit}
              showTypeTitle
              onActionClick={onActionClickHandler}
              preset={preset}
            />
            <LeadersListWidget
              tillAge={leaderboard.tillAge}
              clock={leaderboard.clock}
              leaderboardType="girls"
              limit={limit}
              showTypeTitle
              onActionClick={onActionClickHandler}
              preset={preset}
            />
          </>
        }
        smallLists={
          <LeadersListWidget
            tillAge={leaderboard.tillAge}
            clock={leaderboard.clock}
            leaderboardType={leaderboard.leaderboardType}
            limit={mobileLimit}
            onActionClick={onActionClickHandler}
            preset={preset}
          />
        }
      />
      {leaderboard.showModal && (
        <LeadersModalWidget onClose={leaderboard.closeModal} />
      )}
    </>
  );
}
